import {Network} from '@ethersproject/networks';
import {JsonRpcSigner, Web3Provider} from '@ethersproject/providers';
import WalletConnectProvider from '@walletconnect/web3-provider';
import {BigNumber} from 'ethers';
import Web3Modal from 'web3modal';
import {ChainId} from '../sdk';

type THandler<T> = (arg0: T) => Promise<void>;

export enum Connector {
  Injected = 'injected',
  WalletConnect = 'walletconnect',
}

const providerOptions = {
  [Connector.WalletConnect]: {
    package: WalletConnectProvider,
    options: {
      rpc: {
        [ChainId.BINANCE_MAINNET]: 'https://bsc-dataseed.binance.org/',
        [ChainId.BINANCE_TESTNET]: 'https://data-seed-prebsc-1-s1.binance.org:8545',
        [ChainId.CSC_MAINNET]: 'https://rpc.coinex.net',
        [ChainId.CSC_TESTNET]: 'https://testnet-rpc.coinex.net/',
      },
    },
  },
};

export const web3Modal = new Web3Modal({
  network: 'mainnet',
  cacheProvider: true,
  providerOptions,
});

class WalletService {
  web3ModalProvider?: any;
  provider?: Web3Provider;
  signer?: JsonRpcSigner;
  network?: Network;
  address: string = '';
  chainId: number = 0;
  connectorCache?: Connector;
  isHandlersEnabled?: boolean;

  handleAccountsChanged: THandler<string[]> = async () => {};
  handleChainChanged: THandler<number> = async () => {};
  handleDisconnect: THandler<any> = async () => {};
  accountsChangedListener: THandler<string[]> = async () => {};
  chainChangedListener: THandler<string> = async () => {};

  private async _setupProps(connector: Connector) {
    this.connectorCache = connector;
    this.web3ModalProvider = await web3Modal.connectTo(connector);
    this.provider = new Web3Provider(this.web3ModalProvider);
    this.signer = this.provider.getSigner();
    this.address = await this.signer!.getAddress();
    const network = await this.provider!.getNetwork();
    this.chainId = network.chainId;
  }

  async connect(connector: Connector) {
    this._disableListeners();
    await this._setupProps(connector);
    this._setupListeners();
    this._enableListeners();

    return {
      address: this.address,
      chainId: this.chainId,
    };
  }

  async disconnect() {
    await web3Modal.clearCachedProvider();
    if (this.web3ModalProvider.disconnect && typeof this.web3ModalProvider.disconnect === 'function') {
      await this.web3ModalProvider.disconnect();
    }
  }

  private _enableListeners() {
    if (this.isHandlersEnabled && this.connectorCache !== Connector.WalletConnect) {
      return;
    }

    if (this.web3ModalProvider.on) {
      this.web3ModalProvider.on('accountsChanged', this.accountsChangedListener);
      this.web3ModalProvider.once('chainChanged', this.chainChangedListener);

      this.isHandlersEnabled = true;
    }
  }

  private _disableListeners() {
    if (this.web3ModalProvider && this.web3ModalProvider.off) {
      this.web3ModalProvider.off('accountsChanged', this.accountsChangedListener);
      this.web3ModalProvider.off('chainChanged', this.chainChangedListener);
    }
  }

  initHandlers(
    handleAccountsChanged: THandler<string[]>,
    handleChainChanged: THandler<number>,
    handleDisconnect: THandler<any>,
  ) {
    this.handleAccountsChanged = handleAccountsChanged;
    this.handleChainChanged = handleChainChanged;
    this.handleDisconnect = handleDisconnect;
  }

  _setupListeners() {
    this.accountsChangedListener = async (accounts: string[]) => {
      if (accounts.length) {
        await this.handleAccountsChanged(accounts);
      } else {
        await this.handleDisconnect({});
        this.disconnect();
      }
    };

    this.chainChangedListener = async (chainId: string) => {
      console.log('chainId', chainId);
      this.chainId = BigNumber.from(chainId).toNumber();
      if (this.connectorCache) {
        await this._setupProps(this.connectorCache);
        await this.handleChainChanged(this.chainId);
      } else {
        await this.handleDisconnect({});
        this.disconnect();
      }
      this.isHandlersEnabled = false;
    };
  }
}

const walletService = new WalletService();

export default walletService;
